import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Printer, ArrowUpRight, Download, ShieldCheck, ArrowLeft } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import RateSheetGateModal from '@/components/RateSheetGateModal';
import { TID } from '@/testIds';

const SECTION_1 = [
  { code: 'SQF-FLT', label: 'Flat / architectural',      rate: '$2.50 / sqft',        scope: 'Smooth interior/exterior walls, standard window graphics, glass partitions, flat un-contoured vehicle panels (spot graphics). Highly optimized for sign shops.' },
  { code: 'SQF-COM', label: 'Commercial fleet contours', rate: '$3.00 / sqft',        scope: 'Standard commercial fleet profiles: box trucks, transit cargo vans, utility setups with moderate contour requirements.' },
  { code: 'SQF-CLR', label: 'Color-change restyling',    rate: '$5.00 / sqft',        scope: 'Premium full color-change wrap media with deep edge tucks, extreme contour management, hardware disassembly, high-detail finishing.' },
  { code: 'FLT-SM',  label: 'Compact commercial / coupe',rate: '$650 – $800 / veh',   scope: 'Transit Connect, ProMaster City, NV200, Scion xB, compact utility profiles.', tl: '1.0 day' },
  { code: 'FLT-MD',  label: 'Standard cargo / mid-van',  rate: '$900 – $1,200 / veh', scope: 'Ford Transit 150/250, Chevy Express, GMC Savana, low-roof short-wheelbase.', tl: '1.0 – 1.5 days' },
  { code: 'FLT-LG',  label: 'Extended / high-roof',      rate: '$1,400 – $1,800 / veh', scope: 'Ford Transit Extended High-Roof, Mercedes Sprinter 170" WB, Ram ProMaster High-Roof.', tl: '1.5 – 2.0 days' },
  { code: 'FLT-BOX', label: 'Box truck 14ft – 26ft',     rate: '$85 – $110 / lin.ft',  scope: 'Standard flat rivet panels. Corrugated panels subject to custom quote.', tl: 'Varies by size' },
];

const SECTION_2 = [
  { code: 'PNL-HD',  label: 'Hood',                       rate: '$200 – $275',  scope: 'Compound curve manipulation, complex edge wrapping, precise trim around washer nozzles.' },
  { code: 'PNL-RF',  label: 'Roof (standard)',            rate: '$250 – $350',  scope: 'Sedans, coupes, standard SUVs. Antenna/roof-rack removal billed separately.' },
  { code: 'PNL-DR',  label: 'Side door (per door)',       rate: '$150 – $225',  scope: 'Recessed handle pockets, deep body channel setting.' },
  { code: 'PNL-FND', label: 'Front fender (per side)',    rate: '$175 – $250',  scope: 'Complex stretching over wheel arches, seamless integration around headlight indicators.' },
  { code: 'PNL-QTR', label: 'Rear quarter panel (side)',  rate: '$225 – $325',  scope: 'Seamless flow through complex C/D pillars into roof rail architecture.' },
  { code: 'PNL-TG',  label: 'Tailgate / trunk lid',       rate: '$200 – $300',  scope: 'Deep license plate recesses, handles, clean edge finishing around emblems.' },
  { code: 'PNL-BMP', label: 'Bumper (front or rear)',     rate: '$350 – $500',  scope: 'Deep recesses, fog light pockets, parking sensors, mandatory inlay placement.' },
  { code: 'PNL-MVR', label: 'Side mirrors (pair)',        rate: '$120 – $180',  scope: 'Seamless single-piece execution or hidden precision relief cuts on severe compound contours.' },
];

const SECTION_3 = [
  { code: 'AFT-MTH', label: 'Fleet Care Monthly',        rate: '$75 / mo wholesale',  retail: '$110 – $135 / mo', scope: '12×/yr — pH bath, decontamination, edge audit, polymer top-coat.' },
  { code: 'AFT-QTR', label: 'Pro Shield Quarterly',      rate: '$60 / mo wholesale',  retail: '$95 – $110 / mo',  scope: '4×/yr (monthly billed) — iron decon, finish conditioning, 3-mo polymer sealant.' },
  { code: 'AFT-ANL', label: 'Wrap Rejuvenation Annual',  rate: '$30 / mo wholesale',  retail: '$45 – $55 / mo',   scope: '1×/yr — chemical scaling, clay-mitt, ceramic-infused quartz refresh.' },
];

const SECTION_4 = [
  { code: 'PRO-DAY-01',  label: 'Master installer · full day', rate: '$1,000 / day',  scope: 'Up to 8 hrs on-site or in-house. Maximum efficiency for rolling fleet spot graphics.' },
  { code: 'PRO-HALF-01', label: 'Master installer · half day', rate: '$550 / half day', scope: 'Up to 4 hrs — partial coverage, single-axle trailers, window perf.' },
  { code: 'PRO-HR-01',   label: 'Hourly bench rate',           rate: '$135 / hr',      scope: 'Intricate custom elements, hardware teardowns, emblem re-badging, modifications.' },
  { code: 'ACC-DECAL',   label: 'Cut vinyl / door logos',      rate: '$150 – $250 / kit', scope: 'Commercial spot graphics, corporate ID kits (driver, passenger, rear).' },
  { code: 'ACC-ROOF',    label: 'Commercial roof wrap',        rate: '$300 – $450 / veh', scope: 'Full coverage for high-roof or standard cargo vans.' },
  { code: 'REM-FLT',     label: 'Professional vinyl removal',  rate: '$120 / hr',      scope: 'Mechanical + heat removal, adhesive breakdown, chemical wash to OEM. + disposal fee if applicable.' },
];

const SECTIONS = [
  { id: 'core',    title: 'Section 1 · Foundational labor', subtitle: 'Sqft rates + full-vehicle tiers', rows: SECTION_1 },
  { id: 'panel',   title: 'Section 2 · Panel-by-panel',     subtitle: 'Partial wraps · single-panel repairs · accent packages', rows: SECTION_2 },
  { id: 'aftercare', title: 'Section 3 · Aftercare MRR',    subtitle: 'B2B2C recurring revenue — you auto-bill retail, we auto-bill you', rows: SECTION_3, showRetail: true },
  { id: 'day',     title: 'Section 4 · Day rates & add-ons', subtitle: 'Flexible deployment · specialized add-ons · removal', rows: SECTION_4 },
];

export default function RateCardPage() {
  const [gateOpen, setGateOpen] = useState(false);
  const doPrint = () => window.print();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <RateSheetGateModal open={gateOpen} onClose={() => setGateOpen(false)} />
      <main className="pt-28 pb-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Link to="/" className="mi-eyebrow text-white/50 hover:text-white inline-flex items-center gap-2 mb-8">
            <ArrowLeft className="w-3 h-3" /> BACK TO HOME
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <div className="mi-eyebrow mb-3 text-[#E63946]">// CONFIDENTIAL B2B RATE SHEET</div>
              <h1 className="mi-headline text-5xl md:text-6xl lg:text-7xl">
                Wholesale <span className="text-[#E63946]">Rate Card.</span>
              </h1>
              <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">
                Wholesale, labor-only rates for print production partners, sign shops and agencies. All prices assume
                partner-supplied premium cast media (3M 2080 / IJ180C, Avery MPI 1105) with matching cast overlaminate.
                Volume tiers unlock automatically past <span className="text-white font-semibold">1,500 sqft / contract</span>.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setGateOpen(true)} data-testid={TID.rateGateOpen} className="mi-btn-red">
                <Download className="w-4 h-4" /> Download branded PDF
              </button>
              <button onClick={doPrint} data-testid={TID.ratePrintBtn} className="mi-btn-ghost">
                <Printer className="w-4 h-4" /> Print this page
              </button>
              <Link to="/partners" className="mi-btn-ghost">
                Apply as Partner <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Callout: Master Execution */}
          <div className="mi-surface p-6 lg:p-8 mb-12 border-l-4 border-l-[#E63946]">
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-7 h-7 text-[#E63946] flex-shrink-0" />
              <div>
                <div className="mi-headline text-2xl mb-2">Master Execution Guarantee</div>
                <p className="text-white/75 leading-relaxed">
                  Every install carries our professional warranty against edge-lifting, bubbling or failures stemming
                  from application error. We ship unmarked / blind-shipped so your client only ever sees your brand.
                </p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-14">
            {SECTIONS.map((sec) => (
              <div key={sec.id} data-testid={TID.rateSection(sec.id)}>
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <div className="mi-headline text-3xl">{sec.title}</div>
                    <div className="mi-eyebrow text-white/50 mt-1">{sec.subtitle}</div>
                  </div>
                </div>
                <div className="border mi-line-strong overflow-hidden">
                  <div className="grid grid-cols-12 bg-[#0A0A0A] border-b mi-line-strong text-[10px] mi-eyebrow text-white/60">
                    <div className="col-span-2 p-3">CODE</div>
                    <div className="col-span-3 p-3">SERVICE</div>
                    <div className={sec.showRetail ? 'col-span-2 p-3' : 'col-span-2 p-3'}>WHOLESALE</div>
                    {sec.showRetail && <div className="col-span-2 p-3">SUGGESTED RETAIL</div>}
                    <div className={`${sec.showRetail ? 'col-span-3' : 'col-span-5'} p-3`}>SCOPE / NOTES</div>
                  </div>
                  {sec.rows.map((r) => (
                    <div key={r.code} data-testid={TID.rateRow(r.code)}
                         className="grid grid-cols-12 border-b mi-line hover:bg-white/[0.02] transition-colors">
                      <div className="col-span-2 p-3 font-mono-data text-[13px] text-[#E63946] font-semibold">{r.code}</div>
                      <div className="col-span-3 p-3">
                        <div className="mi-headline text-lg leading-tight">{r.label}</div>
                        {r.tl && <div className="mi-eyebrow text-[10px] text-white/50 mt-1">EST · {r.tl}</div>}
                      </div>
                      <div className="col-span-2 p-3 font-mono-data text-[13px] text-white">{r.rate}</div>
                      {sec.showRetail && <div className="col-span-2 p-3 font-mono-data text-[13px] text-white/60">{r.retail}</div>}
                      <div className={`${sec.showRetail ? 'col-span-3' : 'col-span-5'} p-3 text-[13px] text-white/70 leading-relaxed`}>
                        {r.scope}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Standards & modifiers */}
          <div className="grid md:grid-cols-3 gap-4 mt-14">
            <Panel title="Production standards"
                   items={[
                     'Cast wrap vinyl required (3M 2080/IJ180C, Avery MPI 1105 or equivalent).',
                     'Matching cast overlaminate mandatory.',
                     'Digital proof with panel tiling; 0.5"–1.0" vertical overlap for fleet.',
                     'Vehicle arrives washed, no wax/ceramic coating.',
                   ]} />
            <Panel title="Difficulty modifier"
                   items={[
                     'Severe compound curves, deep-recessed cargo lines, rivets and composite bodywork can add up to +25% over base band.',
                     'Applied only when validated on intake photos/measurements.',
                   ]} />
            <Panel title="Bulk tier"
                   items={[
                     'Projects >1,500 cumulative sqft under a single contract qualify for dynamically-negotiated tier deductions.',
                     'Standing purchase orders welcome — call for Net 30 terms.',
                   ]} />
          </div>

          {/* Bottom CTA */}
          <div className="mt-14 mi-surface p-8 lg:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-l-4 border-l-[#E63946]">
            <div>
              <div className="mi-eyebrow mb-2">// READY TO PARTNER</div>
              <div className="mi-headline text-3xl">Get your custom rate sheet + onboarding kit.</div>
              <div className="mt-2 text-white/60">One business day turnaround. No card, no commitment.</div>
            </div>
            <div className="flex gap-3">
              <Link to="/partners" className="mi-btn-red">Apply as Partner <ArrowUpRight className="w-4 h-4" /></Link>
              <a href="tel:8327912692" className="mi-btn-ghost">Call 832.791.2692</a>
            </div>
          </div>

          <div className="mt-8 mi-eyebrow text-white/30 text-[10px] text-center">
            CONFIDENTIAL B2B RATE SHEET · INTERNAL USE ONLY · MOTOR iNKS LLC · UPDATED {new Date().getFullYear()}
          </div>
        </div>
      </main>
      <Footer />

      <style>{`
        @media print {
          @page { margin: 12mm; }
          header, footer { display: none !important; }
          main { padding-top: 0 !important; }
          body, .App, main { background: #fff !important; color: #111 !important; }
          .mi-surface, [class*="border-"] { background: #fff !important; color: #111 !important; border-color: #ddd !important; }
          .text-white, .text-white\\/70, .text-white\\/60, .text-white\\/85 { color: #111 !important; }
          .text-\\[\\#E63946\\], .text-red-500 { color: #b1000e !important; }
          .mi-btn-red, .mi-btn-ghost, .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function Panel({ title, items }) {
  return (
    <div className="mi-surface p-5">
      <div className="mi-eyebrow mb-3 text-[#E63946]">// {title.toUpperCase()}</div>
      <ul className="space-y-2 text-sm text-white/75">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2"><span className="text-[#E63946]">→</span><span>{it}</span></li>
        ))}
      </ul>
    </div>
  );
}
